import { useMemo } from "react";
import type { CampaignListItem } from "@gen/tracker/v1/campaign_pb";
import type { DataHealth } from "@gen/tracker/v1/data_health_pb";

import { derive, type Bag } from "@/lib/campaigns-table";
import { DASH, formatMoney, formatPct } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { addDays, dayIn } from "@/lib/period";
import { buildToday, clockIn } from "@/lib/today";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useDataHealth } from "@/lib/services/data-health-service";
import { statsRowToNumbers, useReportingSchema } from "@/lib/services/reporting-schema-service";
import { useHourlyTrend, useOwnCampaignIds, useTodayCampaigns, type TodayCampaigns } from "@/lib/services/today-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { issueRows, useNetworkName, type AttentionRow } from "@/components/health/issues";

/** One campaign of the day with its numbers, re-derived through the registry. */
export interface TodayRow {
  id: string;
  name: string;
  v: Bag;
}

/**
 * The state «Сегодня» is in — each one finite (the card's «без вечного
 * скелетона»): `loading` only while a request is on its way, `error` when one
 * failed, `first` when the workspace holds no campaign at all, `ready` else.
 */
export type TodayState = "loading" | "error" | "first" | "ready";

/**
 * The day «Сегодня» draws (доски Today, TodayFirstRun, MToday): the hourly
 * trend over yesterday and today, the day's campaigns ranked and flagged by
 * the server, under the Top bar's «Мои · Все». The day is the workspace's
 * (decision 03); every rate is the registry's (`buildToday`, `derive`).
 */
export function useToday() {
  const { scope } = useReportParams();
  const tz = useReportTimezone();
  const now = new Date();
  const today = dayIn(now, tz);
  const yesterday = addDays(today, -1);
  const clock = clockIn(now, tz);
  const mine = scope === "mine";

  const schema = useReportingSchema();
  const own = useOwnCampaignIds(mine);
  const ids = mine ? own.data : undefined;
  // «Мои» with no campaign of one's own is a day of zeros, asked of nobody.
  const trendEnabled = !mine || (!!ids && ids.length > 0);
  const trend = useHourlyTrend(yesterday, today, tz, ids, trendEnabled);
  const day = useTodayCampaigns(today, tz, mine);

  const model = useMemo(
    () => (schema.data ? buildToday(trendEnabled ? (trend.data ?? []) : [], schema.data, { today, yesterday }, clock) : undefined),
    // The model follows the data and the hour, not the second hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trend.data, trendEnabled, schema.data, today, yesterday, clock.hour],
  );

  const failed = schema.error ?? (trendEnabled ? trend.error : null) ?? day.error ?? (mine ? own.error : null);
  const waiting = schema.isPending || day.isPending || (mine && own.isPending) || (trendEnabled && trend.isPending);
  const state: TodayState = failed && !(model && day.data) ? "error" : waiting ? "loading" : day.data?.total === 0 ? "first" : "ready";

  const retry = () => void Promise.all([schema.refetch(), trend.refetch(), day.refetch(), own.refetch()]);
  return { tz, today, clock, model, trend, day, state, error: failed, retry, updatedAt: Math.max(trend.dataUpdatedAt, day.dataUpdatedAt) };
}

/** A list item's numbers of the day, re-derived through the registry. */
export function useTodayRows(items: CampaignListItem[] | undefined): TodayRow[] {
  const schema = useReportingSchema();
  return useMemo(() => {
    if (!schema.data || !items) return [];
    return items.flatMap((i) => (i.campaign ? [{ id: i.campaign.id, name: i.campaign.name, v: derive(schema.data!.metrics, statsRowToNumbers(i.stats)) }] : []));
  }, [items, schema.data]);
}

/**
 * «Требует внимания»: the campaigns the server flags today — losing money,
 * spending without a click — then the data-health issues. One list for the
 * desktop and the phone; each row leads somewhere, none pauses a campaign
 * (decision 10).
 */
export function useAttention(day: TodayCampaigns | undefined): AttentionRow[] {
  const t = useT();
  const health = useDataHealth();
  const netName = useNetworkName();
  const losing = useTodayRows(day?.losing);
  const noClicks = useTodayRows(day?.noClicks);
  return attentionRows(t, losing, noClicks, health.data, netName);
}

/** The attention rows from the flagged campaigns and the health issues. Pure. */
export function attentionRows(
  t: ReturnType<typeof useT>,
  losing: TodayRow[],
  noClicks: TodayRow[],
  health: DataHealth | undefined,
  netName: (id: string) => string,
): AttentionRow[] {
  const items: AttentionRow[] = [];
  for (const r of losing) {
    items.push({
      tone: "danger",
      title: r.name,
      sub: t("ops.today.att.losing", {
        roi: formatPct(r.v.roi, { digits: 0, signed: true }),
        cost: formatMoney(r.v.cost),
        ar: r.v.approve_rate !== undefined ? formatPct(r.v.approve_rate, { digits: 0 }) : DASH,
      }),
      action: { label: t("ops.open"), to: `/campaigns/${r.id}` },
    });
  }
  for (const r of noClicks) {
    if ((r.v.cost ?? 0) <= 0) continue;
    items.push({
      tone: "warn",
      title: r.name,
      sub: t("ops.today.att.noClicks", { cost: formatMoney(r.v.cost) }),
      action: { label: t("ops.open"), to: `/campaigns/${r.id}` },
    });
  }
  if (health) items.push(...issueRows(health, t, netName));
  return items;
}
